import { useEffect, useState } from 'react';

import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useSelector } from 'react-redux';

import { RootState, useAppDispatch } from 'src/app/store';
import { DropdownMenuContent } from 'src/common/DropdownMenu/DropdownMenuContent';
import { DropdownMenuItem } from 'src/common/DropdownMenu/DropdownMenuItem';
import { TripRead } from 'src/common/external/bambi-api/bambiApi';
import { useCanCapturePaymentMethod } from 'src/common/forms/PaymentMethodCaptureForm/useCanCapturePaymentMethod';
import { Button } from 'src/common/primitives/Button';
import { CancellationMenu } from 'src/features/dispatch/TripDropdownCancellationMenu';
import { useCreateEmptyInvoice } from 'src/features/invoices/InvoiceController';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';

import { useTripMenuActions } from '../../dispatch/DispatchCalendar/useTripMenuActions';
import { setOpenTripDropdownMenuId } from '../../dispatch/dispatcher.slice';
import { DeleteModal } from './DeleteModal';

interface TripGridRowActionsProps {
  trip: TripRead;
  triggerClassName?: string;
}

export const TripGridRowActions = ({
  trip,
  triggerClassName,
}: TripGridRowActionsProps) => {
  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();
  const dispatch = useAppDispatch();
  const openTripDropdownMenuId = useSelector(
    (state: RootState) => state.dispatcher.openTripDropdownMenuId
  );
  const [open, setOpen] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const menuActions = useTripMenuActions(!!openTripDropdownMenuId === false);
  const {
    isLoading: isPaymentFeatureLoading,
    isEnabled: isPaymentFeatureEnabled,
  } = useCanCapturePaymentMethod();
  const handleCreateEmptyInvoice = useCreateEmptyInvoice();

  useEffect(() => {
    if (openTripDropdownMenuId === trip.id) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [openTripDropdownMenuId, trip.id]);

  return (
    <div className="w-[24px]">
      <DropdownMenu.Root
        open={open}
        onOpenChange={(val) => {
          if (!val) dispatch(setOpenTripDropdownMenuId(null));
        }}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            className={triggerClassName}
            variant="ghost"
            dataTestId="row-actions-trigger"
            onClick={() => dispatch(setOpenTripDropdownMenuId(trip.id))}
          >
            <EllipsisVerticalIcon className="h-6 w-6 cursor-pointer" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenuContent side="bottom">
            <DropdownMenuItem onClick={() => menuActions.clone(trip.id)}>
              Clone
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => menuActions.viewEdit(trip.id)}>
              Edit
            </DropdownMenuItem>
            {!shouldShowRideOrderingPortalFeatures && (
              <>
                <CancellationMenu trip={trip} />
                <DropdownMenuItem onClick={() => menuActions.addEditTags(trip)}>
                  Add Tags
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsConfirmingDelete(true)}>
                  Delete
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => menuActions.goToTripSummary(trip.id)}
                >
                  View Summary
                </DropdownMenuItem>
              </>
            )}
            {!isPaymentFeatureLoading && isPaymentFeatureEnabled && (
              <DropdownMenuItem
                onClick={() =>
                  handleCreateEmptyInvoice({
                    line_items: [
                      {
                        description: `Trip - ${trip.id}`,
                        amount_cents: trip.input_price_cents ?? 0,
                        trip: trip.id,
                        line_item_type: 'trip',
                      },
                    ],
                  })
                }
              >
                Create Invoice
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <DeleteModal
        open={isConfirmingDelete}
        setOpen={setIsConfirmingDelete}
        onCancel={() => {
          setIsConfirmingDelete(false);
        }}
        onConfirm={async () => {
          setIsConfirmingDelete(false);
          menuActions.delete(trip.id);
        }}
      />
    </div>
  );
};
